/**
 * BNO trials download links.
 *
 * Turns the inert Download pills on /pages/trials into real installer links. Each card
 * (sections/bno-trials-grid.liquid) ships a catalog slug in data-bno-slug, an empty
 * version label, and one Download <a> per OS with NO href — visibly unavailable. This
 * module fetches the release catalog once, reads each plugin's current version, writes
 * the version label and builds the installer URLs.
 *
 * Design notes:
 * - ONE fetch per page load, shared by every card via a module-level promise. The
 *   catalog is a single small JSON document listing every plugin; fetching it per card
 *   would be ten identical requests.
 * - The catalog does NOT carry installer URLs. They are derived from slug + OS +
 *   version, which is the naming contract of the releases bucket:
 *     https://releases.bno.audio/plugin/<slug>/macos/<slug>-<version>.pkg
 *     https://releases.bno.audio/plugin/<slug>/windows/<slug>-<version>.exe
 * - NO version fallback, deliberately. If the fetch fails, the slug is missing from the
 *   catalog, or the entry carries no version, the button STAYS in its unavailable state
 *   and the version label stays empty. A hardcoded fallback (e.g. "1.0.0") would hand
 *   out a stale or 404 installer with no visible symptom — a silent wrong answer is
 *   worse than a visible dead button. Learn more is untouched and always works.
 * - No layout shift on activation: the pill is already rendered at its final size, and
 *   the version label is an :empty span (display:none until it has text), so filling it
 *   grows the name line rightward without moving anything.
 * - OS detection is NOT here. It runs synchronously in the section's inline script,
 *   before the cards are parsed, and lands as html[data-bno-os]; CSS hides the pill for
 *   the other OS. Doing it here (a deferred module) would add or remove a button after
 *   first paint. This module fills the href on every Download pill it finds, visible or
 *   not, so a theme-editor re-render can never leave the two out of step.
 * - CORS on the bucket already allows bno.audio / www.bno.audio. No credentials, no
 *   custom headers — either would turn this into a preflighted request for nothing.
 */

const RELEASES_ORIGIN = 'https://releases.bno.audio';
const CATALOG_URL = RELEASES_ORIGIN + '/catalog.json';

// The one markup shape, from sections/bno-trials-grid.liquid.
const CARD = '[data-bno-trial]';
const VERSION = '[data-bno-version]';
const DOWNLOAD = '[data-bno-dl]';

// Installer extension per OS key (the data-bno-dl value, which is also the path
// segment). An unknown key yields no URL, so the button stays unavailable.
const EXTENSION = {
  macos: 'pkg',
  windows: 'exe',
};

/**
 * The single in-flight (then settled) catalog request.
 * @type {Promise<Record<string, { version?: string }> | null> | null}
 */
let catalogPromise = null;

/**
 * Fetch the release catalog once and reuse it. Resolves to the `plugins` map, or null on
 * any failure (network, non-2xx, malformed JSON, missing `plugins`) — never rejects, so
 * a caller only has to check for null.
 * @returns {Promise<Record<string, { version?: string }> | null>}
 */
function loadCatalog() {
  if (catalogPromise) return catalogPromise;

  catalogPromise = fetch(CATALOG_URL, { credentials: 'omit' })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      if (!data || typeof data.plugins !== 'object' || data.plugins === null) return null;
      return data.plugins;
    })
    .catch(() => null);

  return catalogPromise;
}

/**
 * Build the installer URL for one slug / OS / version, or '' when the OS key is not one
 * we publish for.
 * @param {string} slug
 * @param {string} os
 * @param {string} version
 * @returns {string}
 */
function installerUrl(slug, os, version) {
  const extension = EXTENSION[os];
  if (!extension) return '';
  return RELEASES_ORIGIN + '/plugin/' + slug + '/' + os + '/' + slug + '-' + version + '.' + extension;
}

/**
 * Fill in one card: write the version label and turn every Download pill into a real
 * link. Dropping role + aria-disabled alongside the href is what makes it focusable and
 * announced as a link again.
 * @param {HTMLElement} card
 * @param {string} slug
 * @param {string} version
 */
function activate(card, slug, version) {
  const label = card.querySelector(VERSION);
  if (label) label.textContent = 'v' + version;

  for (const button of card.querySelectorAll(DOWNLOAD)) {
    const href = installerUrl(slug, button.dataset.bnoDl, version);
    if (!href) continue;
    button.href = href;
    button.removeAttribute('aria-disabled');
    button.removeAttribute('role');
  }
}

/** Enhance every not-yet-enhanced trials card on the page. Idempotent. */
function enhance() {
  const cards = [];

  for (const card of document.querySelectorAll(CARD)) {
    if (card.dataset.enhanced) continue;
    if (!card.querySelector(DOWNLOAD)) continue; // no Download pill — nothing to do
    card.dataset.enhanced = 'true';
    cards.push(card);
  }

  if (!cards.length) return;

  loadCatalog().then((plugins) => {
    // Fetch failed or the catalog is malformed: leave every button unavailable.
    if (!plugins) return;

    for (const card of cards) {
      // A blank slug (the block setting was left empty) is not an error to recover from
      // — there is nothing to look up, so the card stays unavailable.
      const slug = card.dataset.bnoSlug;
      if (!slug) continue;

      const entry = plugins[slug];
      const version = entry && typeof entry.version === 'string' ? entry.version.trim() : '';
      if (!version) continue;

      activate(card, slug, version);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', enhance);
} else {
  enhance();
}

// Theme editor: a section re-render replaces the markup, so enhance the new copy. The
// catalog promise survives, so this costs no extra request.
document.addEventListener('shopify:section:load', enhance);
